/**
 * `/documents` — customer-facing document list + signed-URL download.
 *
 * Phase 3 Stream E of the PSM → Customer Portal migration. Adapted from
 * the legacy PSM `portal/src/pages/DocumentsPage.tsx`; the Tailwind /
 * shadcn / lucide-react / responsive split has been replaced with the
 * inline-style UI primitives shipped by the Phase 1 foundation so the
 * page renders cleanly without taking a Tailwind upgrade as a dep.
 *
 * Download flow (Option A from the migration brief):
 *   1. User clicks the Download button on a row.
 *   2. SPA calls `GET /api/portal/documents/:id/download`.
 *   3. BFF forwards to PSM internal `…/signed-url`, which mints a V4 GCS
 *      URL (TTL ≤ 15 min) and returns `{ url, expires_at, filename }`.
 *   4. SPA opens the URL in a new tab via `window.open(url, '_blank',
 *      'noopener,noreferrer')` — the GCS download begins inline.
 *   5. The row briefly displays "ダウンロード準備中…" → "リンク有効期限:
 *      hh:mm" / "リンクの有効期限が切れました。再度ダウンロードしてください".
 *
 * Why not a 302 redirect?
 *   The migration audit (Phase 1 risk R4) flagged cross-origin redirects
 *   as a session-cookie / log-leak surface. Returning JSON keeps the
 *   portal_session cookie scoped to the BFF and lets the SPA surface
 *   errors inline before navigating.
 *
 * Spec:
 *   - docs/specs/PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.3
 *   - docs/specs/PORTAL_INTERNAL_API_CONTRACT.md §2.6 (D1 / D2)
 *   - docs/specs/PORTAL_GCS_IAM_SSOT.md (signing strategy)
 */
import { useEffect, useMemo, useState } from 'react';
import type { PortalDocument, PortalDocumentSignedUrl } from '@customer-portal/shared';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../components/ui';
import { PortalFeatureDisabledError, apiGet } from '../lib/api';
import { PortalApiError } from '../lib/apiClient';

interface ListEnvelope {
  ok: true;
  data: {
    items: PortalDocument[];
    total: number;
    limit: number;
    offset: number;
  };
}

interface DownloadEnvelope {
  ok: true;
  data: PortalDocumentSignedUrl;
}

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; items: PortalDocument[]; total: number }
  | { kind: 'feature_disabled' }
  | { kind: 'error'; code: string };

type DownloadState =
  | { kind: 'idle' }
  | { kind: 'requesting'; documentId: number }
  | { kind: 'opened'; documentId: number; expiresAt: string; filename: string }
  | { kind: 'error'; documentId: number; code: string };

const DOC_TYPE_LABELS: Record<string, string> = {
  invoice: 'Invoice',
  export_certificate: 'Export certificate',
  bill_of_lading: 'Bill of lading (B/L)',
  inspection_certificate: 'Inspection certificate',
  photo: 'Photo',
};

function docTypeLabel(type: string): string {
  return DOC_TYPE_LABELS[type] ?? type;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
}

function formatExpiry(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
}

function isExpired(value: string): boolean {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() <= Date.now();
}

function docTypeBadge(type: string): JSX.Element {
  // Soft category colors keyed off the documented allowlist; unknown types
  // fall back to a neutral pill so a future PSM-side category can render
  // without a portal release.
  let bg = '#f3f4f6';
  let color = '#374151';
  if (type === 'bill_of_lading') {
    bg = '#dbeafe';
    color = '#1e40af';
  } else if (type === 'invoice') {
    bg = '#d1fae5';
    color = '#065f46';
  } else if (type === 'export_certificate') {
    bg = '#e0e7ff';
    color = '#3730a3';
  } else if (type === 'inspection_certificate') {
    bg = '#fef3c7';
    color = '#92400e';
  } else if (type === 'photo') {
    bg = '#f5f3ff';
    color = '#5b21b6';
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        background: bg,
        color,
      }}
    >
      {docTypeLabel(type)}
    </span>
  );
}

const DOCUMENT_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All types' },
  ...Object.entries(DOC_TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

export function DocumentsPage(): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'idle' });
  const [reloadTick, setReloadTick] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [download, setDownload] = useState<DownloadState>({ kind: 'idle' });

  useEffect(() => {
    const ctrl = new AbortController();
    setLoad({ kind: 'loading' });
    void (async () => {
      try {
        const res = await apiGet<ListEnvelope>('/documents?limit=100', ctrl.signal);
        const items = res.data?.items ?? [];
        setLoad({ kind: 'success', items, total: res.data?.total ?? items.length });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof PortalFeatureDisabledError) {
          setLoad({ kind: 'feature_disabled' });
          return;
        }
        if (err instanceof PortalApiError) {
          setLoad({ kind: 'error', code: err.code });
          return;
        }
        setLoad({ kind: 'error', code: 'UNKNOWN' });
      }
    })();
    return () => ctrl.abort();
  }, [reloadTick]);

  const items = useMemo<PortalDocument[]>(
    () => (load.kind === 'success' ? load.items : []),
    [load],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((doc) => {
      if (typeFilter !== 'all' && doc.document_type !== typeFilter) return false;
      if (q.length === 0) return true;
      if (doc.filename.toLowerCase().includes(q)) return true;
      if (doc.chassis_number?.toLowerCase().includes(q)) return true;
      if (docTypeLabel(doc.document_type).toLowerCase().includes(q)) return true;
      return false;
    });
  }, [items, search, typeFilter]);

  async function handleDownload(doc: PortalDocument): Promise<void> {
    if (!doc.downloadable) return;
    setDownload({ kind: 'requesting', documentId: doc.id });
    try {
      const res = await apiGet<DownloadEnvelope>(`/documents/${doc.id}/download`);
      const url = res.data?.url;
      const expiresAt = res.data?.expires_at;
      if (!url || !expiresAt) {
        setDownload({ kind: 'error', documentId: doc.id, code: 'PORTAL_UPSTREAM_EMPTY' });
        return;
      }
      // window.open keeps the new tab disconnected from the SPA via
      // 'noopener,noreferrer'; the browser handles the download/preview.
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      setDownload({
        kind: 'opened',
        documentId: doc.id,
        expiresAt,
        filename: res.data?.filename ?? doc.filename,
      });
    } catch (err) {
      if (err instanceof PortalApiError) {
        setDownload({ kind: 'error', documentId: doc.id, code: err.code });
        return;
      }
      setDownload({ kind: 'error', documentId: doc.id, code: 'UNKNOWN' });
    }
  }

  return (
    <section data-testid="documents-page" style={{ display: 'grid', gap: '1.25rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
          Documents
        </h1>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
          Documents / Download invoices, certificates and shipping documents.
        </p>
      </header>

      <Card aria-label="Document filters">
        <CardBody>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'end',
              gap: '1rem',
            }}
          >
            <label style={{ flex: '1 1 220px', display: 'grid', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 600 }}>
                Search
              </span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filename, chassis, type…"
                data-testid="documents-search-input"
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: '#fff',
                }}
              />
            </label>
            <label style={{ flex: '0 0 200px', display: 'grid', gap: '0.25rem' }}>
              <span style={{ fontSize: '0.8125rem', color: '#374151', fontWeight: 600 }}>
                Document type
              </span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                data-testid="documents-type-filter"
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: '#fff',
                }}
              >
                {DOCUMENT_TYPE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </CardBody>
      </Card>

      <ListBody
        state={load}
        filtered={filtered}
        download={download}
        onDownload={handleDownload}
        onRetry={() => setReloadTick((n) => n + 1)}
      />
    </section>
  );
}

interface ListBodyProps {
  state: LoadState;
  filtered: PortalDocument[];
  download: DownloadState;
  onDownload: (doc: PortalDocument) => void;
  onRetry: () => void;
}

function ListBody({ state, filtered, download, onDownload, onRetry }: ListBodyProps): JSX.Element {
  if (state.kind === 'idle' || state.kind === 'loading') {
    return (
      <div aria-busy="true" data-testid="documents-loading">
        <Spinner block label="Loading documents" />
      </div>
    );
  }
  if (state.kind === 'feature_disabled') {
    return (
      <div data-testid="documents-feature-disabled">
        <ErrorState
          title="Document downloads are coming soon"
          description="This feature is being prepared. Please try again later."
        />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div data-testid="documents-error">
        <ErrorState
          title="Could not load the document list"
          description={`Error code: ${state.code}`}
          onRetry={onRetry}
          retryLabel="Retry"
        />
      </div>
    );
  }
  if (state.items.length === 0) {
    return (
      <div data-testid="documents-empty">
        <EmptyState
          title="No documents yet"
          description="Invoices, bills of lading, certificates and more will appear here."
        />
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <div data-testid="documents-filtered-empty">
        <EmptyState
          title="No documents match your filters"
          description="Try changing the search term or document type."
        />
      </div>
    );
  }
  return (
    <Card aria-label="Document list">
      <Table caption={`${filtered.length} of ${state.total} documents`}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Filename</TableHeaderCell>
            <TableHeaderCell>Type</TableHeaderCell>
            <TableHeaderCell>Vehicle</TableHeaderCell>
            <TableHeaderCell>Upload date</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell style={{ textAlign: 'right' }}>Actions</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((doc) => (
            <DocumentRow key={doc.id} doc={doc} download={download} onDownload={onDownload} />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

interface DocumentRowProps {
  doc: PortalDocument;
  download: DownloadState;
  onDownload: (doc: PortalDocument) => void;
}

function DocumentRow({ doc, download, onDownload }: DocumentRowProps): JSX.Element {
  const isRowSubject =
    (download.kind === 'requesting' || download.kind === 'opened' || download.kind === 'error') &&
    download.documentId === doc.id;
  const isRequesting = download.kind === 'requesting' && download.documentId === doc.id;

  return (
    <TableRow hover>
      <TableCell>
        <div style={{ fontWeight: 600, color: '#111827' }}>{doc.filename}</div>
        {isRowSubject && download.kind === 'opened' && !isExpired(download.expiresAt) ? (
          <div
            style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#065f46' }}
            data-testid={`documents-download-toast-${doc.id}`}
          >
            Preparing your download... Link valid until {formatExpiry(download.expiresAt)}
          </div>
        ) : null}
        {isRowSubject && download.kind === 'opened' && isExpired(download.expiresAt) ? (
          <div
            style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#b45309' }}
            data-testid={`documents-download-expired-${doc.id}`}
          >
            This link has expired. Please download again.
          </div>
        ) : null}
        {isRowSubject && download.kind === 'error' ? (
          <div
            style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#b91c1c' }}
            data-testid={`documents-download-error-${doc.id}`}
          >
            {downloadErrorMessage(download.code)}
          </div>
        ) : null}
      </TableCell>
      <TableCell>{docTypeBadge(doc.document_type)}</TableCell>
      <TableCell
        style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: '0.8125rem' }}
      >
        {doc.chassis_number ?? '—'}
      </TableCell>
      <TableCell style={{ color: '#4b5563', fontSize: '0.8125rem' }}>
        {formatDate(doc.created_at)}
      </TableCell>
      <TableCell>
        {doc.downloadable ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.6875rem',
              fontWeight: 600,
              background: '#d1fae5',
              color: '#065f46',
            }}
          >
            Available
          </span>
        ) : (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.6875rem',
              fontWeight: 600,
              background: '#f3f4f6',
              color: '#6b7280',
            }}
          >
            Unavailable
          </span>
        )}
      </TableCell>
      <TableCell style={{ textAlign: 'right' }}>
        <Button
          variant="secondary"
          size="sm"
          inline
          disabled={!doc.downloadable || isRequesting}
          aria-disabled={!doc.downloadable || isRequesting}
          data-testid={`documents-download-button-${doc.id}`}
          aria-label={`Download ${doc.filename}`}
          onClick={() => void onDownload(doc)}
        >
          {isRequesting ? 'Loading…' : 'Download'}
        </Button>
      </TableCell>
    </TableRow>
  );
}

function downloadErrorMessage(code: string): string {
  if (code === 'NOT_FOUND')
    return 'Document not found (it may have been deleted, or you do not have permission).';
  if (code === 'PORTAL_INTERNAL_UPSTREAM_UNAVAILABLE')
    return 'Could not reach the PSM internal service. Please wait a moment and try again.';
  if (code === 'PORTAL_AUTH_REQUIRED') return 'Sign-in required.';
  return `Could not retrieve the download URL (${code}).`;
}
